import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import { Alert, AlertDescription } from '../components/ui/alert';
import {
  Mail, MessageSquare, MessageCircle, Plus, Edit, Trash2, Save,
  Send, Settings, Loader2, CheckCircle, AlertCircle, ExternalLink,
  Info, Users, BarChart, Phone, Zap, Link2, Copy, Eye, EyeOff
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

// ============================================================================
// CHANNEL CONFIGURATION SCHEMA
// ============================================================================
const CHANNEL_CONFIG = {
  email: {
    id: 'email',
    name: 'Email Marketing',
    icon: Mail,
    color: 'blue',
    description: 'Send marketing campaigns to your customers via email',
    tabs: ['campaigns', 'templates', 'settings'],
    providers: [
      { id: 'sendgrid', name: 'SendGrid', logo: '📧', features: ['Campaigns', 'Automation', 'Analytics'] },
      { id: 'mailchimp', name: 'Mailchimp', logo: '🐒', features: ['Drag & Drop Editor', 'Segmentation', 'A/B Testing'] },
      { id: 'brevo', name: 'Brevo (Sendinblue)', logo: '💌', features: ['Email + SMS', 'Marketing Automation', 'CRM'] }
    ]
  },
  sms: {
    id: 'sms',
    name: 'SMS Marketing',
    icon: MessageSquare,
    color: 'green',
    description: 'Reach customers instantly via SMS with MSG91',
    tabs: ['templates', 'campaigns', 'settings'],
    provider: { id: 'msg91', name: 'MSG91', logo: '📱' }
  },
  whatsapp: {
    id: 'whatsapp',
    name: 'Aniyor WhatsApp',
    icon: MessageCircle,
    color: 'emerald',
    description: 'Connect with customers via WhatsApp Business API',
    tabs: ['overview', 'settings'],
    provider: { id: 'aniyor', name: 'Aniyor WhatsApp CRM', logo: '💬' }
  }
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================
const UniversalCampaignBuilder = () => {
  const { user, apiCall, isAdmin, isSuperAdmin } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Get initial channel from URL query params
  const initialChannel = searchParams.get('channel') || 'sms';
  
  // Global state
  const [activeChannel, setActiveChannel] = useState(initialChannel);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Update URL when channel changes
  const handleChannelChange = (channel) => {
    setActiveChannel(channel);
    setSearchParams({ channel });
  };
  
  // SMS State
  const [smsConfig, setSmsConfig] = useState({
    auth_key: '',
    sender_id: '',
    flow_id: '',
    template_id: '',
    template_content: '',
    is_active: false,
    test_status: null
  });
  const [smsTemplates, setSmsTemplates] = useState([]);
  const [smsCampaigns, setSmsCampaigns] = useState([]);
  const [editingSmsTemplate, setEditingSmsTemplate] = useState(null);
  const [smsActiveTab, setSmsActiveTab] = useState('templates');
  
  // Email State
  const [emailConfig, setEmailConfig] = useState({
    provider: '',
    api_key: '',
    from_email: '',
    from_name: '',
    is_active: false
  });
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [emailCampaigns, setEmailCampaigns] = useState([]);
  const [emailActiveTab, setEmailActiveTab] = useState('campaigns');
  
  // WhatsApp State
  const [whatsappConfig, setWhatsappConfig] = useState({
    business_name: '',
    phone_number: '',
    business_category: '',
    website_url: '',
    description: '',
    is_connected: false,
    connection_status: 'not_started'
  });
  const [whatsappActiveTab, setWhatsappActiveTab] = useState('overview');
  
  // Show password states
  const [showSmsAuthKey, setShowSmsAuthKey] = useState(false);
  const [showEmailApiKey, setShowEmailApiKey] = useState(false);

  // ============================================================================
  // DATA FETCHING
  // ============================================================================
  const fetchSmsData = useCallback(async () => {
    try {
      const [templatesRes, campaignsRes, configRes] = await Promise.all([
        apiCall('/api/sms/templates').catch(() => ({ templates: [] })),
        apiCall('/api/sms/campaigns').catch(() => []),
        apiCall('/api/sms/config').catch(() => ({}))
      ]);
      
      setSmsTemplates(templatesRes?.templates || templatesRes || []);
      setSmsCampaigns(Array.isArray(campaignsRes) ? campaignsRes : []);
      if (configRes && Object.keys(configRes).length > 0) {
        setSmsConfig(prev => ({ ...prev, ...configRes }));
      }
    } catch (error) {
      console.error('Error fetching SMS data:', error);
    }
  }, [apiCall]);

  const fetchEmailData = useCallback(async () => {
    try {
      const [configRes] = await Promise.all([
        apiCall('/api/email/config').catch(() => ({}))
      ]);
      
      if (configRes && Object.keys(configRes).length > 0) {
        setEmailConfig(prev => ({ ...prev, ...configRes }));
      }
    } catch (error) {
      console.error('Error fetching email data:', error);
    }
  }, [apiCall]);

  const fetchWhatsappData = useCallback(async () => {
    try {
      const configRes = await apiCall('/api/whatsapp/config').catch(() => ({}));
      if (configRes && Object.keys(configRes).length > 0) {
        setWhatsappConfig(prev => ({ ...prev, ...configRes }));
      }
    } catch (error) {
      console.error('Error fetching WhatsApp data:', error);
    }
  }, [apiCall]);

  useEffect(() => {
    const loadAllData = async () => {
      setLoading(true);
      await Promise.all([fetchSmsData(), fetchEmailData(), fetchWhatsappData()]);
      setLoading(false);
    };
    loadAllData();
  }, [fetchSmsData, fetchEmailData, fetchWhatsappData]);

  // ============================================================================
  // SMS HANDLERS
  // ============================================================================
  const handleSaveSmsConfig = async () => {
    setSaving(true);
    try {
      await apiCall('/api/sms/config', {
        method: 'POST',
        body: JSON.stringify(smsConfig)
      });
      toast.success('SMS configuration saved successfully');
    } catch (error) {
      toast.error(error.message || 'Failed to save SMS configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleTestSms = async () => {
    setSaving(true);
    try {
      const result = await apiCall('/api/sms/test', { method: 'POST' });
      setSmsConfig(prev => ({ ...prev, test_status: result.success ? 'success' : 'failed' }));
      toast.success('Test SMS sent successfully');
    } catch (error) {
      setSmsConfig(prev => ({ ...prev, test_status: 'failed' }));
      toast.error(error.message || 'Test SMS failed');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSmsTemplate = async (template) => {
    setSaving(true);
    try {
      await apiCall('/api/sms/templates', {
        method: 'POST',
        body: JSON.stringify(template)
      });
      toast.success('Template saved successfully');
      setEditingSmsTemplate(null);
      fetchSmsData();
    } catch (error) {
      toast.error(error.message || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSmsTemplate = async (templateId) => {
    if (!confirm('Are you sure you want to delete this template?')) return;
    try {
      await apiCall(`/api/sms/templates/${templateId}`, { method: 'DELETE' });
      toast.success('Template deleted');
      fetchSmsData();
    } catch (error) {
      toast.error(error.message || 'Failed to delete template');
    }
  };

  // ============================================================================
  // EMAIL HANDLERS
  // ============================================================================
  const handleSaveEmailConfig = async () => {
    setSaving(true);
    try {
      await apiCall('/api/email/marketing/config', {
        method: 'POST',
        body: JSON.stringify(emailConfig)
      });
      toast.success('Email configuration saved successfully');
    } catch (error) {
      toast.error(error.message || 'Failed to save email configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleTestEmail = async () => {
    setSaving(true);
    try {
      await apiCall('/api/email/marketing/test', { method: 'POST' });
      toast.success('Test email sent successfully');
    } catch (error) {
      toast.error(error.message || 'Test email failed');
    } finally {
      setSaving(false);
    }
  };

  // ============================================================================
  // WHATSAPP HANDLERS
  // ============================================================================
  const handleSaveWhatsappConfig = async () => {
    setSaving(true);
    try {
      await apiCall('/api/whatsapp/config', {
        method: 'POST',
        body: JSON.stringify(whatsappConfig)
      });
      toast.success('WhatsApp configuration saved');
      setWhatsappConfig(prev => ({ ...prev, connection_status: 'pending_review' }));
    } catch (error) {
      toast.error(error.message || 'Failed to save WhatsApp configuration');
    } finally {
      setSaving(false);
    }
  };

  // ============================================================================
  // PERMISSION CHECKS
  // ============================================================================
  const canManageSettings = isAdmin() || isSuperAdmin;
  const canManageTemplates = isAdmin() || isSuperAdmin;

  // ============================================================================
  // LOADING STATE
  // ============================================================================
  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64" data-testid="campaign-builder-loading">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      </DashboardLayout>
    );
  }

  // ============================================================================
  // RENDER FUNCTIONS
  // ============================================================================
  
  // SMS Templates Tab
  const renderSmsTemplates = () => (
    <div className="space-y-4" data-testid="sms-templates-section">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold">SMS Templates</h2>
        {canManageTemplates && (
          <Button
            data-testid="sms-add-template-btn"
            onClick={() => setEditingSmsTemplate({ id: '', name: '', type: 'transactional', content: '', dlt_template_id: '' })}
          >
            <Plus className="h-4 w-4 mr-2" /> Add Template
          </Button>
        )}
      </div>

      <Alert className="bg-blue-50 border-blue-200">
        <Info className="h-4 w-4 text-blue-600" />
        <AlertDescription className="text-blue-800">
          <strong>DLT Compliance:</strong> All SMS templates must be DLT registered for India. Use variables like <code className="bg-blue-100 px-1 rounded">##variable##</code> that match your DLT-approved templates.
        </AlertDescription>
      </Alert>

      {smsTemplates.length === 0 ? (
        <Card className="p-8 text-center" data-testid="sms-templates-empty">
          <MessageSquare className="h-12 w-12 mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-medium text-gray-700">No Templates Yet</h3>
          <p className="text-gray-500 mt-1">Create your first SMS template to get started</p>
          {canManageTemplates && (
            <Button
              className="mt-4"
              data-testid="sms-create-first-template-btn"
              onClick={() => setEditingSmsTemplate({ id: '', name: '', type: 'transactional', content: '', dlt_template_id: '' })}
            >
              <Plus className="h-4 w-4 mr-2" /> Create Template
            </Button>
          )}
        </Card>
      ) : (
        <div className="grid gap-4">
          {smsTemplates.map((template) => (
            <Card key={template.id} data-testid={`sms-template-card-${template.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="font-semibold">{template.name}</h3>
                      <Badge variant="outline" className="text-xs">{template.type}</Badge>
                      {template.status === 'active' && (
                        <Badge className="bg-green-100 text-green-700 text-xs">Active</Badge>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 bg-gray-50 p-2 rounded font-mono">
                      {template.content}
                    </p>
                    {template.dlt_template_id && (
                      <p className="text-xs text-gray-500 mt-2">DLT Template ID: {template.dlt_template_id}</p>
                    )}
                  </div>
                  {canManageTemplates && (
                    <div className="flex gap-2 ml-4">
                      <Button
                        variant="ghost"
                        size="sm"
                        data-testid={`sms-edit-template-${template.id}`}
                        onClick={() => setEditingSmsTemplate(template)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-600"
                        data-testid={`sms-delete-template-${template.id}`}
                        onClick={() => handleDeleteSmsTemplate(template.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* SMS Template Edit Modal */}
      {editingSmsTemplate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="sms-template-modal">
          <Card className="w-full max-w-lg mx-4">
            <CardHeader>
              <CardTitle>{editingSmsTemplate.id ? 'Edit Template' : 'New Template'}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Template Name</Label>
                <Input
                  data-testid="sms-template-name-input"
                  value={editingSmsTemplate.name}
                  onChange={(e) => setEditingSmsTemplate({ ...editingSmsTemplate, name: e.target.value })}
                  placeholder="e.g., Order Confirmation"
                />
              </div>
              <div className="space-y-2">
                <Label>Type</Label>
                <select
                  data-testid="sms-template-type-select"
                  className="w-full h-10 rounded-md border px-3 text-sm"
                  value={editingSmsTemplate.type}
                  onChange={(e) => setEditingSmsTemplate({ ...editingSmsTemplate, type: e.target.value })}
                >
                  <option value="otp">OTP</option>
                  <option value="transactional">Transactional</option>
                  <option value="promotional">Promotional</option>
                </select>
              </div>
              <div className="space-y-2">
                <Label>DLT Template ID</Label>
                <Input
                  data-testid="sms-template-dlt-input"
                  value={editingSmsTemplate.dlt_template_id}
                  onChange={(e) => setEditingSmsTemplate({ ...editingSmsTemplate, dlt_template_id: e.target.value })}
                  placeholder="Enter DLT approved template ID"
                />
              </div>
              <div className="space-y-2">
                <Label>Template Content</Label>
                <Textarea
                  data-testid="sms-template-content-textarea"
                  value={editingSmsTemplate.content}
                  onChange={(e) => setEditingSmsTemplate({ ...editingSmsTemplate, content: e.target.value })}
                  placeholder="Your OTP is ##number##. Valid for 5 mins."
                  rows={4}
                />
                <p className="text-xs text-gray-500">Use ##variable## for dynamic content</p>
              </div>
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  data-testid="sms-template-cancel-btn"
                  onClick={() => setEditingSmsTemplate(null)}
                >
                  Cancel
                </Button>
                <Button
                  data-testid="sms-template-save-btn"
                  onClick={() => handleSaveSmsTemplate(editingSmsTemplate)}
                  disabled={saving}
                >
                  {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                  Save Template
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );

  // SMS Campaigns Tab
  const renderSmsCampaigns = () => (
    <div className="space-y-4" data-testid="sms-campaigns-section">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold">SMS Campaigns</h2>
        <Button data-testid="sms-create-campaign-btn">
          <Plus className="h-4 w-4 mr-2" /> Create Campaign
        </Button>
      </div>

      {smsCampaigns.length === 0 ? (
        <Card className="p-8 text-center" data-testid="sms-campaigns-empty">
          <MessageSquare className="h-12 w-12 mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-medium text-gray-700">No Campaigns Yet</h3>
          <p className="text-gray-500 mt-1">Create your first SMS campaign to engage with customers</p>
          <Button className="mt-4" data-testid="sms-create-first-campaign-btn">
            <Plus className="h-4 w-4 mr-2" /> Create Campaign
          </Button>
        </Card>
      ) : (
        <div className="grid gap-4">
          {smsCampaigns.map((campaign) => (
            <Card key={campaign.id} data-testid={`sms-campaign-card-${campaign.id}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold">{campaign.name}</h3>
                    <p className="text-sm text-gray-500">Sent to {campaign.recipients || 0} recipients</p>
                  </div>
                  <Badge>{campaign.status}</Badge>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );

  // SMS Settings Tab (Admin Only)
  const renderSmsSettings = () => (
    <div className="space-y-4" data-testid="sms-settings-section">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            MSG91 Configuration
          </CardTitle>
          <CardDescription>Configure your MSG91 SMS gateway settings</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Alert className="bg-amber-50 border-amber-200">
            <AlertCircle className="h-4 w-4 text-amber-600" />
            <AlertDescription className="text-amber-800">
              Get your MSG91 credentials from{' '}
              <a href="https://control.msg91.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                MSG91 Dashboard <ExternalLink className="inline h-3 w-3" />
              </a>
            </AlertDescription>
          </Alert>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Auth Key *</Label>
              <div className="relative">
                <Input
                  data-testid="sms-auth-key-input"
                  type={showSmsAuthKey ? 'text' : 'password'}
                  value={smsConfig.auth_key}
                  onChange={(e) => setSmsConfig({ ...smsConfig, auth_key: e.target.value })}
                  placeholder="MSG91 Auth Key"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                  onClick={() => setShowSmsAuthKey(!showSmsAuthKey)}
                >
                  {showSmsAuthKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Sender ID *</Label>
              <Input
                data-testid="sms-sender-id-input"
                value={smsConfig.sender_id}
                onChange={(e) => setSmsConfig({ ...smsConfig, sender_id: e.target.value })}
                placeholder="e.g., ANIYOR"
              />
            </div>
            <div className="space-y-2">
              <Label>Flow ID *</Label>
              <Input
                data-testid="sms-flow-id-input"
                value={smsConfig.flow_id}
                onChange={(e) => setSmsConfig({ ...smsConfig, flow_id: e.target.value })}
                placeholder="MSG91 Flow ID"
              />
            </div>
            <div className="space-y-2">
              <Label>OTP Template ID *</Label>
              <Input
                data-testid="sms-template-id-input"
                value={smsConfig.template_id}
                onChange={(e) => setSmsConfig({ ...smsConfig, template_id: e.target.value })}
                placeholder="Template ID from MSG91"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>OTP Template Content (DLT Approved)</Label>
            <Textarea
              data-testid="sms-otp-template-textarea"
              value={smsConfig.template_content}
              onChange={(e) => setSmsConfig({ ...smsConfig, template_content: e.target.value })}
              placeholder="Your OTP is ##number##. Valid for 5 mins."
              rows={3}
            />
            <p className="text-xs text-gray-500">Paste your exact DLT-approved template. The ##number## variable will be replaced with the actual OTP.</p>
          </div>

          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                data-testid="sms-active-checkbox"
                checked={smsConfig.is_active}
                onChange={(e) => setSmsConfig({ ...smsConfig, is_active: e.target.checked })}
                className="rounded"
              />
              <span>Enable SMS Gateway</span>
            </label>

            {smsConfig.test_status === 'success' && (
              <Badge className="bg-green-100 text-green-700">
                <CheckCircle className="h-3 w-3 mr-1" /> Tested Successfully
              </Badge>
            )}
            {smsConfig.test_status === 'failed' && (
              <Badge variant="destructive">Test Failed</Badge>
            )}
          </div>

          <div className="flex gap-2 pt-4 border-t">
            <Button
              data-testid="sms-save-config-btn"
              onClick={handleSaveSmsConfig}
              disabled={saving}
            >
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              Save Configuration
            </Button>
            <Button
              variant="outline"
              data-testid="sms-test-btn"
              onClick={handleTestSms}
              disabled={saving || !smsConfig.auth_key}
            >
              <Send className="h-4 w-4 mr-2" />
              Send Test SMS
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  // Email Campaigns Tab
  const renderEmailCampaigns = () => (
    <div className="space-y-4" data-testid="email-campaigns-section">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold">Email Campaigns</h2>
        <Button data-testid="email-create-campaign-btn" disabled={!emailConfig.is_active}>
          <Plus className="h-4 w-4 mr-2" /> Create Campaign
        </Button>
      </div>

      {!emailConfig.is_active ? (
        <Alert className="bg-amber-50 border-amber-200">
          <AlertCircle className="h-4 w-4 text-amber-600" />
          <AlertDescription className="text-amber-800">
            Please configure your email provider in the Settings tab before creating campaigns.
          </AlertDescription>
        </Alert>
      ) : null}

      <Card className="p-8 text-center" data-testid="email-campaigns-empty">
        <Mail className="h-12 w-12 mx-auto text-gray-300 mb-4" />
        <h3 className="text-lg font-medium text-gray-700">No Campaigns Yet</h3>
        <p className="text-gray-500 mt-1">Create your first email campaign to reach your customers</p>
        <Button className="mt-4" data-testid="email-create-first-campaign-btn" disabled={!emailConfig.is_active}>
          <Plus className="h-4 w-4 mr-2" /> Create Campaign
        </Button>
      </Card>
    </div>
  );

  // Email Templates Tab
  const renderEmailTemplates = () => (
    <div className="space-y-4" data-testid="email-templates-section">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold">Email Templates</h2>
        <Button data-testid="email-create-template-btn" disabled={!emailConfig.is_active}>
          <Plus className="h-4 w-4 mr-2" /> Create Template
        </Button>
      </div>

      <Alert className="bg-blue-50 border-blue-200">
        <Info className="h-4 w-4 text-blue-600" />
        <AlertDescription className="text-blue-800">
          <strong>Note:</strong> Transactional emails (order confirmations, password resets) are handled by ZeptoMail. 
          This section is for <strong>marketing emails</strong> only.
        </AlertDescription>
      </Alert>

      <Card className="p-8 text-center" data-testid="email-templates-empty">
        <Mail className="h-12 w-12 mx-auto text-gray-300 mb-4" />
        <h3 className="text-lg font-medium text-gray-700">No Templates Yet</h3>
        <p className="text-gray-500 mt-1">Create reusable email templates for your campaigns</p>
      </Card>
    </div>
  );

  // Email Settings Tab
  const renderEmailSettings = () => (
    <div className="space-y-4" data-testid="email-settings-section">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Email Provider Configuration
          </CardTitle>
          <CardDescription>Configure your email marketing provider</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <Alert className="bg-blue-50 border-blue-200">
            <Info className="h-4 w-4 text-blue-600" />
            <AlertDescription className="text-blue-800">
              <strong>ZeptoMail</strong> is already configured for transactional emails. 
              Choose a provider below for <strong>marketing campaigns</strong>.
            </AlertDescription>
          </Alert>

          {/* Provider Selection */}
          <div className="space-y-3">
            <Label>Select Provider</Label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {CHANNEL_CONFIG.email.providers.map((provider) => (
                <div
                  key={provider.id}
                  data-testid={`email-provider-${provider.id}`}
                  onClick={() => setEmailConfig({ ...emailConfig, provider: provider.id })}
                  className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                    emailConfig.provider === provider.id
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="text-2xl mb-2">{provider.logo}</div>
                  <h4 className="font-semibold">{provider.name}</h4>
                  <ul className="text-xs text-gray-500 mt-2 space-y-1">
                    {provider.features.map((f, i) => (
                      <li key={f.id || f.name || `${f.title || "f"}-${i}`}>• {f}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          {emailConfig.provider && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>API Key *</Label>
                  <div className="relative">
                    <Input
                      data-testid="email-api-key-input"
                      type={showEmailApiKey ? 'text' : 'password'}
                      value={emailConfig.api_key}
                      onChange={(e) => setEmailConfig({ ...emailConfig, api_key: e.target.value })}
                      placeholder={`${emailConfig.provider} API Key`}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                      onClick={() => setShowEmailApiKey(!showEmailApiKey)}
                    >
                      {showEmailApiKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>From Email *</Label>
                  <Input
                    data-testid="email-from-email-input"
                    type="email"
                    value={emailConfig.from_email}
                    onChange={(e) => setEmailConfig({ ...emailConfig, from_email: e.target.value })}
                    placeholder="marketing@yourdomain.com"
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label>From Name</Label>
                  <Input
                    data-testid="email-from-name-input"
                    value={emailConfig.from_name}
                    onChange={(e) => setEmailConfig({ ...emailConfig, from_name: e.target.value })}
                    placeholder="Your Store Name"
                  />
                </div>
              </div>

              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    data-testid="email-active-checkbox"
                    checked={emailConfig.is_active}
                    onChange={(e) => setEmailConfig({ ...emailConfig, is_active: e.target.checked })}
                    className="rounded"
                  />
                  <span>Enable Email Marketing</span>
                </label>
              </div>

              <div className="flex gap-2 pt-4 border-t">
                <Button
                  data-testid="email-save-config-btn"
                  onClick={handleSaveEmailConfig}
                  disabled={saving}
                >
                  {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                  Save Configuration
                </Button>
                <Button
                  variant="outline"
                  data-testid="email-test-btn"
                  onClick={handleTestEmail}
                  disabled={saving || !emailConfig.api_key}
                >
                  <Send className="h-4 w-4 mr-2" />
                  Send Test Email
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );

  // WhatsApp Overview Tab
  const renderWhatsappOverview = () => (
    <div className="space-y-6" data-testid="whatsapp-overview-section">
      {/* Hero Section */}
      <Card className="border-l-4 border-l-emerald-500 bg-gradient-to-r from-emerald-50 to-white">
        <CardContent className="pt-6">
          <div className="flex items-start gap-4">
            <div className="bg-emerald-100 p-3 rounded-full">
              <MessageCircle className="h-8 w-8 text-emerald-600" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h2 className="text-2xl font-bold text-emerald-900">Aniyor WhatsApp CRM</h2>
                {whatsappConfig.is_connected ? (
                  <Badge className="bg-emerald-100 text-emerald-700">
                    <CheckCircle className="h-3 w-3 mr-1" /> Connected
                  </Badge>
                ) : (
                  <Badge variant="outline">Not Connected</Badge>
                )}
              </div>
              <p className="text-emerald-800 mb-4">
                Leverage Aniyor&apos;s integrated WhatsApp Business API to automate customer communications, 
                send order updates, and engage customers - all from one platform.
              </p>
              <div className="flex gap-3">
                <Button
                  data-testid="whatsapp-connect-btn"
                  onClick={() => setWhatsappActiveTab('settings')}
                >
                  {whatsappConfig.is_connected ? 'Manage Connection' : 'Connect WhatsApp Business'}
                </Button>
                <Button variant="outline" data-testid="whatsapp-docs-btn">
                  <ExternalLink className="h-4 w-4 mr-2" />
                  View Documentation
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Features Grid */}
      <div>
        <h3 className="text-lg font-semibold mb-4">What You Can Do</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            { icon: Send, title: 'Automated Messages', desc: 'Send order confirmations, shipping updates, and delivery notifications automatically' },
            { icon: Users, title: 'Customer Engagement', desc: 'Re-engage customers with abandoned cart reminders and promotional offers' },
            { icon: MessageCircle, title: 'Two-Way Communication', desc: 'Chat with customers in real-time, answer queries, and provide support' },
            { icon: BarChart, title: 'Analytics & Insights', desc: 'Track message delivery, read rates, and customer engagement metrics' }
          ].map((feature, idx) => (
            <Card key={feature.id || feature.name || `${feature.title || "feature"}-${idx}`} className="hover:shadow-md transition-shadow">
              <CardContent className="pt-6">
                <div className="flex items-start gap-3">
                  <div className="bg-emerald-100 p-2 rounded-lg">
                    <feature.icon className="h-5 w-5 text-emerald-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">{feature.title}</h4>
                    <p className="text-sm text-gray-600">{feature.desc}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Use Cases */}
      <Card>
        <CardHeader>
          <CardTitle>Popular Use Cases</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {[
              'Order confirmation messages', 'Shipping status updates', 'Delivery notifications',
              'Abandoned cart recovery', 'Product launch announcements', 'Flash sale alerts',
              'Customer support chat', 'Feedback collection'
            ].map((useCase, idx) => (
              <div key={useCase.id || useCase.name || `${useCase.title || "useCase"}-${idx}`} className="flex items-center gap-2 text-sm">
                <CheckCircle className="h-4 w-4 text-emerald-500" />
                <span>{useCase}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );

  // WhatsApp Settings Tab
  const renderWhatsappSettings = () => (
    <div className="space-y-4" data-testid="whatsapp-settings-section">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Link2 className="h-5 w-5" />
            WhatsApp Business API Setup
          </CardTitle>
          <CardDescription>Connect your business to WhatsApp Business API through Aniyor</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {whatsappConfig.connection_status === 'connected' ? (
            <Alert className="bg-emerald-50 border-emerald-200">
              <CheckCircle className="h-4 w-4 text-emerald-600" />
              <AlertDescription className="text-emerald-800">
                Your WhatsApp Business account is connected and active!
              </AlertDescription>
            </Alert>
          ) : whatsappConfig.connection_status === 'pending_review' ? (
            <Alert className="bg-amber-50 border-amber-200">
              <Loader2 className="h-4 w-4 text-amber-600 animate-spin" />
              <AlertDescription className="text-amber-800">
                Your application is under review. This typically takes 1-3 business days.
              </AlertDescription>
            </Alert>
          ) : (
            <Alert className="bg-blue-50 border-blue-200">
              <Info className="h-4 w-4 text-blue-600" />
              <AlertDescription className="text-blue-800">
                Fill in your business details below to apply for WhatsApp Business API access.
              </AlertDescription>
            </Alert>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Business Name *</Label>
              <Input
                data-testid="whatsapp-business-name-input"
                value={whatsappConfig.business_name}
                onChange={(e) => setWhatsappConfig({ ...whatsappConfig, business_name: e.target.value })}
                placeholder="Your registered business name"
              />
            </div>
            <div className="space-y-2">
              <Label>Business Phone Number *</Label>
              <Input
                data-testid="whatsapp-phone-input"
                value={whatsappConfig.phone_number}
                onChange={(e) => setWhatsappConfig({ ...whatsappConfig, phone_number: e.target.value })}
                placeholder="+91 XXXXXXXXXX"
              />
            </div>
            <div className="space-y-2">
              <Label>Business Category</Label>
              <Input
                data-testid="whatsapp-category-input"
                value={whatsappConfig.business_category}
                onChange={(e) => setWhatsappConfig({ ...whatsappConfig, business_category: e.target.value })}
                placeholder="e.g., Retail, Fashion, Electronics"
              />
            </div>
            <div className="space-y-2">
              <Label>Website URL</Label>
              <Input
                data-testid="whatsapp-website-input"
                value={whatsappConfig.website_url}
                onChange={(e) => setWhatsappConfig({ ...whatsappConfig, website_url: e.target.value })}
                placeholder="https://yourstore.com"
              />
            </div>
            <div className="md:col-span-2 space-y-2">
              <Label>Business Description</Label>
              <Textarea
                data-testid="whatsapp-description-textarea"
                value={whatsappConfig.description}
                onChange={(e) => setWhatsappConfig({ ...whatsappConfig, description: e.target.value })}
                placeholder="Brief description of your business"
                rows={3}
              />
            </div>
          </div>

          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <h4 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
              <Phone className="h-4 w-4" /> Requirements
            </h4>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• Verified business with Facebook Business Manager</li>
              <li>• Dedicated phone number (not used for personal WhatsApp)</li>
              <li>• Business verification documents (GST, PAN, etc.)</li>
              <li>• Approval typically takes 1-3 business days</li>
            </ul>
          </div>

          <div className="flex gap-2 pt-4 border-t">
            <Button
              data-testid="whatsapp-save-config-btn"
              onClick={handleSaveWhatsappConfig}
              disabled={saving || !whatsappConfig.business_name || !whatsappConfig.phone_number}
            >
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              {whatsappConfig.connection_status === 'not_started' ? 'Submit Application' : 'Update Details'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  // ============================================================================
  // MAIN RENDER
  // ============================================================================
  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-6xl mx-auto" data-testid="universal-campaign-builder">
        {/* Page Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Marketing Campaigns</h1>
            <p className="text-gray-500 mt-1">Manage Email, SMS, and WhatsApp marketing from one place</p>
          </div>
        </div>

        {/* Channel Tabs */}
        <Tabs value={activeChannel} onValueChange={handleChannelChange}>
          <TabsList className="grid w-full grid-cols-3 lg:w-auto lg:inline-grid">
            {Object.values(CHANNEL_CONFIG).map((channel) => {
              const Icon = channel.icon;
              return (
                <TabsTrigger
                  key={channel.id}
                  value={channel.id}
                  data-testid={`channel-tab-${channel.id}`}
                  className="flex items-center gap-2"
                >
                  <Icon className="h-4 w-4" />
                  <span className="hidden sm:inline">{channel.name}</span>
                  <span className="sm:hidden">{channel.id.toUpperCase()}</span>
                </TabsTrigger>
              );
            })}
          </TabsList>

          {/* SMS Channel */}
          <TabsContent value="sms" className="mt-6">
            <Card className="mb-4 border-green-200 bg-green-50/50">
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="bg-green-100 p-2 rounded-lg">
                      <MessageSquare className="h-5 w-5 text-green-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold">SMS Marketing via MSG91</h3>
                      <p className="text-sm text-gray-600">DLT-compliant SMS campaigns for Indian customers</p>
                    </div>
                  </div>
                  <Badge variant={smsConfig.is_active ? 'default' : 'secondary'} className={smsConfig.is_active ? 'bg-green-500' : ''}>
                    {smsConfig.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
              </CardContent>
            </Card>

            <Tabs value={smsActiveTab} onValueChange={setSmsActiveTab}>
              <TabsList>
                <TabsTrigger value="templates" data-testid="sms-tab-templates">Templates</TabsTrigger>
                <TabsTrigger value="campaigns" data-testid="sms-tab-campaigns">Campaigns</TabsTrigger>
                {canManageSettings && (
                  <TabsTrigger value="settings" data-testid="sms-tab-settings">Settings</TabsTrigger>
                )}
              </TabsList>

              <TabsContent value="templates" className="mt-4">{renderSmsTemplates()}</TabsContent>
              <TabsContent value="campaigns" className="mt-4">{renderSmsCampaigns()}</TabsContent>
              {canManageSettings && (
                <TabsContent value="settings" className="mt-4">{renderSmsSettings()}</TabsContent>
              )}
            </Tabs>
          </TabsContent>

          {/* Email Channel */}
          <TabsContent value="email" className="mt-6">
            <Card className="mb-4 border-blue-200 bg-blue-50/50">
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="bg-blue-100 p-2 rounded-lg">
                      <Mail className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold">Email Marketing</h3>
                      <p className="text-sm text-gray-600">
                        {emailConfig.provider 
                          ? `Connected via ${CHANNEL_CONFIG.email.providers.find(p => p.id === emailConfig.provider)?.name || emailConfig.provider}`
                          : 'Choose a provider to get started'}
                      </p>
                    </div>
                  </div>
                  <Badge variant={emailConfig.is_active ? 'default' : 'secondary'} className={emailConfig.is_active ? 'bg-blue-500' : ''}>
                    {emailConfig.is_active ? 'Active' : 'Not Configured'}
                  </Badge>
                </div>
              </CardContent>
            </Card>

            <Tabs value={emailActiveTab} onValueChange={setEmailActiveTab}>
              <TabsList>
                <TabsTrigger value="campaigns" data-testid="email-tab-campaigns">Campaigns</TabsTrigger>
                <TabsTrigger value="templates" data-testid="email-tab-templates">Templates</TabsTrigger>
                {canManageSettings && (
                  <TabsTrigger value="settings" data-testid="email-tab-settings">Settings</TabsTrigger>
                )}
              </TabsList>

              <TabsContent value="campaigns" className="mt-4">{renderEmailCampaigns()}</TabsContent>
              <TabsContent value="templates" className="mt-4">{renderEmailTemplates()}</TabsContent>
              {canManageSettings && (
                <TabsContent value="settings" className="mt-4">{renderEmailSettings()}</TabsContent>
              )}
            </Tabs>
          </TabsContent>

          {/* WhatsApp Channel */}
          <TabsContent value="whatsapp" className="mt-6">
            <Card className="mb-4 border-emerald-200 bg-emerald-50/50">
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="bg-emerald-100 p-2 rounded-lg">
                      <MessageCircle className="h-5 w-5 text-emerald-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold">Aniyor WhatsApp CRM</h3>
                      <p className="text-sm text-gray-600">WhatsApp Business API integration</p>
                    </div>
                  </div>
                  <Badge 
                    variant={whatsappConfig.is_connected ? 'default' : 'secondary'} 
                    className={whatsappConfig.is_connected ? 'bg-emerald-500' : ''}
                  >
                    {whatsappConfig.is_connected ? 'Connected' : 
                     whatsappConfig.connection_status === 'pending_review' ? 'Pending Review' : 'Not Connected'}
                  </Badge>
                </div>
              </CardContent>
            </Card>

            <Tabs value={whatsappActiveTab} onValueChange={setWhatsappActiveTab}>
              <TabsList>
                <TabsTrigger value="overview" data-testid="whatsapp-tab-overview">Overview</TabsTrigger>
                <TabsTrigger value="settings" data-testid="whatsapp-tab-settings">Setup</TabsTrigger>
              </TabsList>

              <TabsContent value="overview" className="mt-4">{renderWhatsappOverview()}</TabsContent>
              <TabsContent value="settings" className="mt-4">{renderWhatsappSettings()}</TabsContent>
            </Tabs>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default UniversalCampaignBuilder;
